import { Form } from 'antd'
import { match } from 'ts-pattern'
import { Trans, useLingui } from '@lingui/react/macro'

import DescriptionForm from './_components/DescriptionForm'
import ReferenceForm from './_components/ReferenceForm'
import RecommendForm from './_components/RecommendForm'

import type { FormInstance } from 'antd'
import type {
  MusicGeneratorFormValuesNew,
  TaskType,
} from '@/pages/tad.ai/ai-music-generator/_types/form'
import type { TadTabItem } from '@/pages/tad.ai/ai-music-generator/_components/Tabs'

import Tabs from '@/pages/tad.ai/_components/Tabs'
import { useAiMusicGeneratorStoreFromZustand } from '@/pages/tad.ai/ai-music-generator/_store/createStore'
import { useMusicFormStore } from '@/pages/tad.ai/ai-music-generator/_store/useMusicFormStore'


import { useState, useEffect, useLayoutEffect } from 'react';
import { cls } from 'tagged-classnames-free';
interface IMusicStyleProps {
  value?: string
  form: FormInstance<MusicGeneratorFormValuesNew>
  onChange?: (value: string) => void
}

const MusicStyle = (props: IMusicStyleProps) => {
  const { value, onChange, form } = props
  const {
    customRecommends,
    updateState,
    lyricsCustomTextareaContent: lyrics,
    reloadRecommend,
  } = useAiMusicGeneratorStoreFromZustand()
  const styleMode = Form.useWatch('styleMode', form)
  const promptTabsMode =
    Form.useWatch('promptTabsMode', form) ||
    form.getFieldValue('promptTabsMode') ||
    'smart'
  const customTaskType = (Form.useWatch('customTaskType') as TaskType) || 'Song'
  const showCustomRecommendBtn =
    promptTabsMode === 'custom' && !!lyrics.trim() && customTaskType === 'Song'
  const [showCustomRecommend, setShowCustomRecommend] = useState(
    promptTabsMode === 'smart',
  ) //custom模式默认不展示recommend
  const [activeKey, setActiveKey] = useState(
    promptTabsMode === 'smart' ? 'recommend' : 'prompt',
  )
  const { t } = useLingui()
  const setFormValues = useMusicFormStore((s) => s.setFormValues)

  useEffect(() => {
    //是否应该出现recommend tab的判断逻辑
    setShowCustomRecommend(
      promptTabsMode === 'smart' || !!customRecommends.length,
    )
  }, [promptTabsMode, customRecommends.length])

  useLayoutEffect(() => {
    //切换smart/custom tab以及切换custom tab下的song/instrument tab时的默认选中tab的设置逻辑
    const activeKey =
      promptTabsMode === 'smart' ||
      reloadRecommend ||
      (showCustomRecommend && customTaskType === 'Song')
        ? 'recommend'
        : 'prompt'
    setActiveKey(activeKey)
    form.setFieldValue('styleMode', activeKey)
    setFormValues({ styleMode: activeKey })
  }, [promptTabsMode, showCustomRecommend, customTaskType, reloadRecommend])

  const items = match(promptTabsMode)
    .with('smart', () => [
      {
        label: t`Recommendeds`,
        key: 'recommend',
      },
      {
        label: t`Prompt`,
        key: 'prompt',
      },
    ])
    .with('custom', () => {
      return match(customTaskType)
        .with(
          'Song',
          () =>
            [
              //customRecommends有值说明之前生成过，也展示tab
              showCustomRecommend || customRecommends.length
                ? {
                    label: t`Recommendeds`,
                    key: 'recommend',
                  }
                : null,
              {
                label: t`Prompt`,
                key: 'prompt',
              },
              {
                label: t`Reference`,
                key: 'reference',
              },
            ].filter(Boolean) as TadTabItem[],
        )
        .with('Instrumental', () => [
          {
            label: t`Prompt`,
            key: 'prompt',
          },
          {
            label: t`Reference`,
            key: 'reference',
          },
        ])
        .exhaustive()
    })
    .exhaustive()

  return (
    <div>
      <div className='text-f-text mb-4 flex items-center justify-between text-lg font-semibold leading-7'>
        <Trans>Style </Trans>
        {showCustomRecommendBtn && (
          <span
            onClick={() => {
              setShowCustomRecommend(true)
              updateState('reloadRecommend', true) //触发recommend组件内部拉取recommend内容
            }}
            className='text-f-text-tertiary relative z-[1] flex h-5 cursor-pointer gap-1 rounded-lg px-2 py-0 text-xs'
          >
            <span className='i-cus--tad-ai-generated size-4' />
            <Trans>AI style Recommendation</Trans>
          </span>
        )}
      </div>
      <Form.Item name='styleMode'>
        <Tabs
          activeKey={activeKey}
          items={items}
          onChange={(v) => setActiveKey(v as string)}
          className={cls`w-full [&>div]:text-[16px]`}
        />
      </Form.Item>
      <Form.Item noStyle dependencies={['styleMode']}>
        {(form: FormInstance<MusicGeneratorFormValuesNew>) => {
          return match(activeKey)
            .with('recommend', () => <RecommendForm form={form} />)
            .with('prompt', () => <DescriptionForm form={form} />)
            .otherwise(() => <ReferenceForm form={form} />)
        }}
      </Form.Item>
    </div>
  )
}

export default MusicStyle
